import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { ColumnAdapter } from '@makyoapp/makyo-common'
import { Tabs } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { DEFAULT_COLUMN_TYPES } from '@/shared/builder'
import { BUILDER_DEVICES } from '@/shared/builder-constants'
import { ComponentPanelTab } from '@/shared/builder-ui'
import { BuilderOverlay, formatDrawerSize, getDrawerSize } from '@/shared/overlay'
import { debounce } from '@/shared/utils'

import { OverlaySettingBehavior } from './OverlaySettingBehavior'
import { OverlaySettingData } from './OverlaySettingData'
import { OverlaySettingDesign } from './OverlaySettingDesign'
import { SearchField } from './SearchField'

/**
 * Overlay Settings Panel
 */
export default function OverlaySetting({ overlay, index }: { overlay: BuilderOverlay; index: number }) {
  const { t } = useTranslation()
  const { updateOverlay, activeOverlays } = useAppOverlaysContext()
  const { tables } = useDbClientContext()

  const [searchData, setSearchData] = useState('')
  const [searchDesign, setSearchDesign] = useState('')
  // const [searchAction, setSearchAction] = useState('')
  const [searchBehavior, setSearchBehavior] = useState('')

  let initialActiveKey: ComponentPanelTab.Data = ComponentPanelTab.Data

  const [activeKey, setActiveKey] = useState<ComponentPanelTab>(initialActiveKey)

  const { device } = useBreakpoint()
  // const ActiveDeviceIcon = BUILDER_DEVICES[device].Icon

  // State lokal untuk mengelola data overlay yang sedang diedit
  const [localOverlay, setLocalOverlay] = useState<BuilderOverlay>(overlay)
  // const [showIconPicker, setShowIconPicker] = useBoolean(false)
  const [tempTitle, setTempTitle] = useState(overlay.title)
  const [tempSizeValue, setTempSizeValue] = useState<number>(getDrawerSize(overlay.size[device]).v1)
  const [tempHeightValue, setTempHeightValue] = useState<number>(() => {
    const size = overlay.size[device]
    const [width, height] = size.split(',')

    // If height is undefined or "undefined", return default value
    if (!height || height === 'undefined') {
      return 50
    }

    // Remove % and parse as integer, fallback to 50 if parsing fails
    const heightValue = parseInt(height.replace('%', ''))
    return isNaN(heightValue) ? 50 : heightValue
  })
  const currentSizeRef = useRef<string>(localOverlay.size[device])

  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Update localOverlay ketika overlay prop berubah
  useEffect(() => {
    // Hanya update jika tidak ada perubahan lokal
    if (!hasLocalChanges) {
      setLocalOverlay(overlay)
    }
  }, [overlay, hasLocalChanges])

  // console.log(localOverlay.bgColor, overlay.bgColor)

  // Update temp values ketika localOverlay berubah
  useEffect(() => {
    setTempTitle(localOverlay.title)
    setTempSizeValue(getDrawerSize(localOverlay.size[device]).v1)

    const size = localOverlay.size[device]
    const [width, height] = size.split(',')
    if (!height || height === 'undefined') {
      setTempHeightValue(50)
    } else {
      const heightValue = parseInt(height.replace('%', ''))
      setTempHeightValue(isNaN(heightValue) ? 50 : heightValue)
    }
  }, [localOverlay, device])

  const isTblPage = useMemo(() => {
    return Boolean(localOverlay.tableId)
  }, [localOverlay.tableId])

  useEffect(() => {
    setTempTitle(overlay.title)
  }, [overlay.title])

  const pageTable = useMemo(() => {
    return localOverlay.tableId ? tables.find((tb) => tb.id === localOverlay.tableId) : undefined
  }, [localOverlay.tableId, tables])

  const colOptions = useMemo(() => {
    return [
      { id: '', name: 'Select Column' } as ColumnAdapter,
      ...((pageTable &&
        pageTable.columns.filter((c) => {
          return DEFAULT_COLUMN_TYPES.includes(c.type)
        })) ||
        []),
    ]
  }, [pageTable])

  const [updatedValue, setUpdatedValue] = useState<{ key: string; value: any } | null>(null)
  const [isApplying, setIsApplying] = useState(false)

  const updateLocalAndAPI = useCallback(
    (updater: (overlay: BuilderOverlay) => void) => {
      setLocalOverlay((prev) => {
        const updated = { ...prev }
        updater(updated)
        return updated
      })
      updateOverlay(index, updater)
      setHasLocalChanges(true)
    },
    [updateOverlay, index]
  )

  const localOverlayRef = useRef(localOverlay)
  useEffect(() => {
    localOverlayRef.current = localOverlay
  }, [localOverlay])

  // Create a debounced update function for size changes
  const debouncedSizeUpdate = useCallback(
    debounce((newSize: number) => {
      const currentOverlay = localOverlayRef.current
      const currentSize = currentSizeRef.current

      const value = formatDrawerSize('side', currentSize, `${newSize}%`)
      currentSizeRef.current = value

      const updatedOverlay = { ...currentOverlay }

      const size = {
        ...localOverlay.size,
        [device]: value,
      }

      updateOverlay(index, (prev) => {
        Object.assign(prev, updatedOverlay)
        prev.size = size
      })

      setUpdatedValue({ key: 'size', value })

      setHasLocalChanges(true)
    }, 250),
    [device, index, updateOverlay]
  )

  // Create a debounced update function for height changes
  const debouncedHeightUpdate = useCallback(
    debounce((newHeight: number) => {
      const currentOverlay = localOverlayRef.current
      const currentSize = currentSizeRef.current

      const value = formatDrawerSize('center', currentSize, `${newHeight}%`)
      currentSizeRef.current = value

      const updatedOverlay = { ...currentOverlay }

      const size = {
        ...currentOverlay.size,
        [device]: value,
      }

      updateOverlay(index, (prev) => {
        Object.assign(prev, updatedOverlay)
        prev.size = size
      })

      setUpdatedValue({ key: 'size', value })
      setHasLocalChanges(true)
    }, 250),
    [device, index, updateOverlay]
  )

  const handleSizeChange = useCallback(
    (val: number[]) => {
      setTempSizeValue(val[0])
      debouncedSizeUpdate(val[0])
    },
    [debouncedSizeUpdate]
  )

  const handleHeightChange = useCallback(
    (val: number[]) => {
      setTempHeightValue(val[0])
      debouncedHeightUpdate(val[0])
    },
    [debouncedHeightUpdate]
  )

  // Apply changes to the overlay
  const handleApplyChanges = async () => {
    if (!updatedValue) return

    setIsApplying(true)

    updateLocalAndAPI((prev) => {
      const key = updatedValue.key as keyof BuilderOverlay
      const prevValues = prev[key]

      const updatedPrevValues = Object.keys(prevValues).reduce(
        (acc, device) => {
          acc[device] = updatedValue.value
          return acc
        },
        {} as typeof prevValues
      )

      // @ts-expect-error
      prev[key] = { ...updatedPrevValues }
    })

    setIsApplying(false)

    setUpdatedValue(null)
    setHasLocalChanges(false) // Reset flag
  }

  const isCenter = localOverlay.placement[device] === 'center'

  const ButtonApplyToAll = () => {
    return (
      <>
        <hr className='mt-2 mb-4 bdr' />

        <div className='w-[calc(100% - 24px)] mb-4 flex justify-center'>
          <button
            className={clsx(
              'text-gray-100 text-xs py-2 px-3 rounded-sm w-full transition-colors',
              isApplying
                ? 'bg-primary-700 dark:bg-primary-700/85 cursor-wait'
                : 'bg-primary-500 dark:bg-primary-500/85 hover:bg-primary-600'
            )}
            onClick={handleApplyChanges}
            disabled={isApplying}
          >
            {isApplying ? t('Saving changes...') : t('Save changes across all screen sizes')}
          </button>
        </div>
      </>
    )
  }

  return (
    <Tabs activeKey={activeKey} defaultActiveKey={ComponentPanelTab.Data}>
      <Tabs.Nav
        onSelect={(eventKey) => {
          setActiveKey((eventKey as ComponentPanelTab) ?? ComponentPanelTab.Data)
        }}
      >
        <Tabs.NavItem eventKey={ComponentPanelTab.Data} className='mk-builder-component-nav-item'>
          <Trans>Data</Trans>
        </Tabs.NavItem>
        <Tabs.NavItem eventKey={ComponentPanelTab.Design} className='mk-builder-component-nav-item'>
          <Trans>Design</Trans>
        </Tabs.NavItem>
        {/* <Tabs.NavItem eventKey={ComponentPanelTab.Action} className='mk-builder-component-nav-item'>
          <Trans>Action</Trans>
        </Tabs.NavItem> */}
        <Tabs.NavItem eventKey={ComponentPanelTab.Behavior} className='mk-builder-component-nav-item'>
          <Trans>Behavior</Trans>
        </Tabs.NavItem>
      </Tabs.Nav>
      <Tabs.Panel eventKey='data' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchData} setSearch={setSearchData} />
        <OverlaySettingData overlay={overlay} index={index} search={searchData} />
      </Tabs.Panel>
      <Tabs.Panel eventKey='design' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchDesign} setSearch={setSearchDesign} />
        <OverlaySettingDesign overlay={overlay} index={index} search={searchDesign} />
      </Tabs.Panel>
      {/* <Tabs.Panel eventKey='action' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchAction} setSearch={setSearchAction} />
        <OverlaySettingAction overlay={overlay} index={index} search={searchAction} />
      </Tabs.Panel> */}
      <Tabs.Panel eventKey='behavior' mountOnEnter unmountOnExit noPadding>
        <SearchField search={searchBehavior} setSearch={setSearchBehavior} />
        <OverlaySettingBehavior overlay={overlay} index={index} search={searchBehavior} />
      </Tabs.Panel>
    </Tabs>

    // NOTE: old accordion
    // <Accordion openMultiple={false} initialActiveItems={['page-settings']}>
    //   <AccordionItem itemId='page-settings' title={<Trans>Title & Icon</Trans>}>
    //     <Field>
    //       <label className='w-[60px]'>
    //         <Trans>Title</Trans>
    //       </label>
    //       {isTblPage && (
    //         <SelectDropdown
    //           id='page-title-columns-source'
    //           withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
    //           onChange={async (col) => {
    //             updateLocalAndAPI((prev) => {
    //               prev.title = col.id
    //             })
    //           }}
    //           value={colOptions.find((col) => localOverlay.title === col.id)}
    //           optionLabel={(col) => `${col?.id} ${col?.name}`}
    //           options={colOptions}
    //           renderOption={(opt) => {
    //             return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
    //           }}
    //         />
    //       )}
    //       {!isTblPage && (
    //         <SimpleInput
    //           value={tempTitle}
    //           onChange={(e) => {
    //             const value = e.currentTarget.value
    //             if (value.trim().length > MAX_OVERLAY_TITLE_LENGTH) {
    //               setTempTitle(value.slice(0, MAX_OVERLAY_TITLE_LENGTH))
    //             } else {
    //               setTempTitle(value)
    //             }
    //           }}
    //           error={
    //             tempTitle.trim().length < MIN_OVERLAY_TITLE_LENGTH ? (
    //               <Trans>Required at least 2 characters.</Trans>
    //             ) : tempTitle.trim().length > MAX_OVERLAY_TITLE_LENGTH ? (
    //               <Trans>Max {MAX_OVERLAY_TITLE_LENGTH} characters.</Trans>
    //             ) : undefined
    //           }
    //           onBlur={async () => {
    //             if (tempTitle.trim() !== localOverlay.title.trim() && tempTitle.length >= MIN_OVERLAY_TITLE_LENGTH) {
    //               setTempTitle(tempTitle.trim())
    //               updateLocalAndAPI((prev) => {
    //                 prev.title = tempTitle.trim()
    //               })
    //             }
    //           }}
    //         />
    //       )}
    //     </Field>

    //     {/* //*Note: Overlay Slug is hidden for now */}
    //     {/* <Field>
    //       <label className='w-[60px]'>
    //         <Trans>Slug</Trans>
    //       </label>
    //       <div className='flex w-full pl-3'>
    //         <SimpleInput
    //           id='page-slug'
    //           className='rtl:[direction:ltr] w-full'
    //           inputClassName='w-full'
    //           value={slug}
    //           onChange={onInputChange}
    //           error={!validSlug ? <Trans>Invalid slug name</Trans> : slugError ? <Trans>{slugError}</Trans> : undefined}
    //         />
    //         <button onClick={handleUpdateSlug} disabled={slugLoading || !validSlug} className='mt-2 ml-2 h-fit'>
    //           {slugLoading ? (
    //             <span className='loading spin' />
    //           ) : (
    //             <CheckOrCross value={validSlug && slugError == undefined} />
    //           )}
    //         </button>
    //       </div>
    //     </Field> */}

    //     {!isTblPage && (
    //       <Field>
    //         <label className='w-full'>
    //           <Trans>Icon</Trans>
    //         </label>
    //         <MaterialIconPicker
    //           searchMs={300}
    //           searchFilter={async (keyword) => {
    //             const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
    //             return aiSearch
    //           }}
    //           onClose={() => setShowIconPicker(false)}
    //           show={showIconPicker}
    //           clickAsDone
    //           onToggle={() => setShowIconPicker(true)}
    //           onDone={async (icon) => {
    //             updateLocalAndAPI((prev) => {
    //               prev.icon = icon
    //             })
    //             setShowIconPicker(false)
    //           }}
    //           value={localOverlay.icon}
    //           className='h-fit rounded-md bg-zinc-200 dark:bg-slate-600'
    //           toggleClassName='p-1.5'
    //           portalMenu
    //           settings={{
    //             fill: 0,
    //             opsz: 24,
    //             wght: 300,
    //             grade: 0,
    //           }}
    //         />
    //       </Field>
    //     )}
    //   </AccordionItem>

    //   <AccordionItem itemId='bgColor' title={<Trans>Background</Trans>}>
    //     <Field isVertical>
    //       <label>Background Color</label>
    //       <BrandKitColorsDropdown
    //         type='bg'
    //         value={localOverlay.bgColor}
    //         onChange={async (opt) => {
    //           updateLocalAndAPI((prev) => {
    //             prev.bgColor = opt
    //           })
    //         }}
    //       />
    //     </Field>

    //     <Field isVertical>
    //       <label>Backdrop Transparancy</label>
    //       <Slider
    //         values={[Number(localOverlay.opacity)]}
    //         max={100}
    //         min={30}
    //         step={1}
    //         onChange={(val) => {
    //           updateLocalAndAPI((prev) => {
    //             prev.opacity = val[0]
    //           })
    //         }}
    //         renderLabel={(val) => `${val}%`}
    //       />
    //     </Field>
    //   </AccordionItem>

    //   <AccordionItem
    //     itemId='size'
    //     title={<Trans>Size</Trans>}
    //     righSection={<ActiveDeviceIcon className='w-3.5 h-3.5' />}
    //   >
    //     <Field isVertical>
    //       <label>{getOverlaySizeLabel(localOverlay.placement[device]!)}</label>
    //       <Slider
    //         values={[tempSizeValue]}
    //         max={100}
    //         min={10}
    //         step={1}
    //         onChange={handleSizeChange}
    //         renderLabel={(val) => `${val}%`}
    //       />
    //     </Field>

    //     {isCenter && (
    //       <Field isVertical>
    //         <label>Height</label>
    //         <Slider
    //           values={[tempHeightValue]}
    //           max={100}
    //           min={10}
    //           step={1}
    //           onChange={handleHeightChange}
    //           renderLabel={(val) => `${val}%`}
    //         />
    //       </Field>
    //     )}

    //     {updatedValue?.key === 'size' && <ButtonApplyToAll />}
    //   </AccordionItem>

    //   <AccordionItem
    //     itemId='placement'
    //     title={<Trans>Direction</Trans>}
    //     righSection={<ActiveDeviceIcon className='w-3.5 h-3.5' />}
    //   >
    //     <Field>
    //       <div className='flex gap-2'>
    //         <SingleIconSelect
    //           options={overlayPlacements.map((p) => ({
    //             id: p.value,
    //             icon: p.icon,
    //             title: getDirectionLabel(p.value),
    //           }))}
    //           selected={switchDirectionPlacement(localOverlay.placement[device]!)}
    //           onClick={(opt) => {
    //             updateLocalAndAPI((prev) => {
    //               const newPlacement = switchDirectionPlacement(opt.id)
    //               prev.placement = {
    //                 ...prev.placement,
    //                 [device]: newPlacement,
    //               }
    //               setUpdatedValue({ key: 'placement', value: newPlacement })
    //             })
    //           }}
    //         />
    //       </div>
    //     </Field>

    //     {updatedValue?.key === 'placement' && <ButtonApplyToAll />}
    //   </AccordionItem>

    //   <AccordionItem
    //     itemId='shape'
    //     title={<Trans>Corner Radius</Trans>}
    //     righSection={<ActiveDeviceIcon className='w-3.5 h-3.5' />}
    //   >
    //     <Field>
    //       <SelectDropdown
    //         value={overlayShapeOptions.find((opt) => opt.value === localOverlay.shape[device])}
    //         options={overlayShapeOptions}
    //         renderOption={(opt) => opt.label}
    //         onChange={(opt) => {
    //           updateLocalAndAPI((prev) => {
    //             prev.shape = {
    //               ...prev.shape,
    //               [device]: opt.value,
    //             }
    //             setUpdatedValue({ key: 'shape', value: opt.value })
    //           })
    //         }}
    //       />
    //     </Field>

    //     {updatedValue?.key === 'shape' && <ButtonApplyToAll />}
    //   </AccordionItem>

    //   <AccordionItem itemId='alignment' title={<Trans>Alignment</Trans>}>
    //     <Field>
    //       <label>Content Alignment</label>

    //       <SingleIconSelect
    //         id='overlay-alignment'
    //         onClick={async (opt) => {
    //           updateLocalAndAPI((prev) => {
    //             prev.alignment = opt.id as any
    //           })
    //         }}
    //         options={[
    //           {
    //             icon: <AlignLeft16 />,
    //             id: 'start',
    //             title: 'Start',
    //           },
    //           {
    //             icon: <AlignCenter16 />,
    //             id: 'center',
    //             title: 'Center',
    //           },
    //           {
    //             icon: <AlignRight16 />,
    //             id: 'end',
    //             title: 'End',
    //           },
    //         ]}
    //         selected={
    //           ['start', 'center', 'end'].includes(localOverlay.alignment as string)
    //             ? (localOverlay.alignment as string)
    //             : 'center'
    //         }
    //       />
    //     </Field>
    //   </AccordionItem>
    // </Accordion>
  )
}
